import React, { useState, useRef, useEffect } from 'react';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DrawOutlined as DrawOutlinedIcon } from "@mui/icons-material";
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import { PDFDocument } from 'pdf-lib';
import axios from 'axios';
import {
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_get_file_upload,
  API_get_user_signature,
} from '../../../constants';
import { getDefaultAxiosConfig, getRightworkAPI } from '../requests/utilities/requests';
import { useNotifyMobileModal } from '../take_training_employee/useNotifyMobileModal';
import AdoptSignatureModal, { renderTypedSignatureToPng, fontFamilyForKey } from './AdoptSignatureModal';
import {
  SIGNATURE_DISCLOSURE_VERSION,
  SIGNATURE_DISCLOSURE_HEADING,
  SIGNATURE_DISCLOSURE_POINTS,
  SIGNATURE_DISCLOSURE_FOOTER,
  SIGNATURE_CONSENT_LABEL,
} from './signatureDisclosure';

// Local-only signature used to demo the flow in preview mode. Never persisted.
const PREVIEW_SIGNATURE = { method: 'typed', typed_name: 'Sample Signature', typed_font: 'cursive' };

function SignatureReviewModal(props) {
  const { open, handleClose, pdfUrl, pdfName, fieldLocations, totalPages, onComplete, isPreview } = props;

  // Hides the native top-right close X in the mobile WebView so taps near
  // that corner don't accidentally close the whole training modal while the
  // user is signing. Employee completion flow renders this modal (via
  // Signature.js complete mode), so the inner_modal_open/closed messages
  // need to fire here, not on SignatureModal (which is admin-authoring only).
  useNotifyMobileModal(open);

  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [fields, setFields] = useState([]);
  const [containerScale, setContainerScale] = useState(1);
  // Real per-page dimensions in PDF points (from pdf-lib), driving the preview's
  // aspect ratio and field scale so coordinates line up on any page size.
  const [pageDimensions, setPageDimensions] = useState([]);

  // Briefly highlighted field after jumping to it via "Next field".
  const [highlightedFieldId, setHighlightedFieldId] = useState(null);

  // Text input modal (signature fields use the adopted reusable signature).
  const [showTextInput, setShowTextInput] = useState(false);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');

  // Reusable adopted signature (typed/drawn), applied to each signature field —
  // same mechanism as the acknowledgement flow. { uuid, method, typed_name,
  // typed_font, image_url } or null until adopted.
  const [userSignature, setUserSignature] = useState(null);
  const [openAdopt, setOpenAdopt] = useState(false);
  // Field awaiting a signature once the user adopts one (first-time use).
  const [pendingFieldId, setPendingFieldId] = useState(null);

  const pdfPreviewRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // The page currently shown — its real point dimensions drive the preview's
  // aspect ratio (falls back to US Letter before the PDF has loaded).
  const currentPageSize = pageDimensions[currentPage] || { width: 612, height: 792 };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: '98%', sm: '98%', md: '70%' },
    maxWidth: { xs: '100%', md: 1200 },
    // height: { xs: '100vh', md: 'auto' },
    maxHeight: '98vh',
    bgcolor: "background.paper",
    border: { xs: 'none', md: "2px solid #E2E8F0" },
    borderRadius: { xs: "10px", md: "15px" },
    boxShadow: 24,
    overflowY: "auto",
  };

  // Initialize fields from fieldLocations
  useEffect(() => {
    if (open && fieldLocations) {
      const initialFields = fieldLocations.map((field, index) => ({
        ...field,
        id: field.id || `field-${index}`,
        filled: false,
        value: null
      }));
      setFields(initialFields);
      setHighlightedFieldId(null);
      setCheckboxChecked(false);
      setHasAgreed(false);
      setIsCompleted(false);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError(null);
      setCurrentPage(0);
      setPendingFieldId(null);
    }
  }, [open, fieldLocations]);

  // Load the signer's adopted reusable signature so signature fields apply with
  // one tap (same as the acknowledgement flow). Non-fatal; they can adopt inline.
  useEffect(() => {
    const loadSignature = async () => {
      if (!open || isPreview || !props.homeCompany) return;
      try {
        const res = await getRightworkAPI(API_get_user_signature, { company_id: props.homeCompany });
        setUserSignature(res?.data?.redux?.payload?.user_signature || null);
      } catch (e) {
        setUserSignature(null);
      }
    };
    loadSignature();
  }, [open, isPreview, props.homeCompany]);

  // Load real per-page dimensions so the preview matches the PDF proportions
  // (and the burned coordinates line up) on any page size, not just US Letter.
  useEffect(() => {
    let cancelled = false;
    const loadDims = async () => {
      if (!open || !pdfUrl) return;
      try {
        const buf = await fetch(pdfUrl).then((r) => r.arrayBuffer());
        const doc = await PDFDocument.load(buf);
        if (!cancelled) setPageDimensions(doc.getPages().map((p) => p.getSize()));
      } catch (e) {
        if (!cancelled) setPageDimensions([]); // fall back to Letter aspect
      }
    };
    loadDims();
    return () => {
      cancelled = true;
    };
  }, [open, pdfUrl]);

  // Track container size for field scaling. Field base sizes are in PDF points;
  // scaling by (containerWidth / pageWidthInPoints) makes a point-sized field
  // render at its true fraction of the page on screen AND match the coordinates
  // pdf-lib burns into the output — for any page size, not just Letter.
  useEffect(() => {
    if (!pdfContainerRef.current) return;

    const updateScale = () => {
      if (pdfContainerRef.current) {
        const containerWidth = pdfContainerRef.current.offsetWidth;
        const pageWidth = (pageDimensions[currentPage]?.width) || 612;
        setContainerScale(containerWidth / pageWidth);
      }
    };

    // Initial calculation
    updateScale();

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(pdfContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasAgreed, open, currentPage, pageDimensions]); // Re-run on page change too

  // Get fields for current page
  const getFieldsByPage = (pageNumber) => {
    return fields.filter(field => field.page === pageNumber);
  };

  // Change page
  const changePage = (direction) => {
    if (direction === 'next' && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Unfilled fields in reading order (by page, then top-to-bottom). On a long
  // PDF with a couple of buried fields, this powers the "Next field" guide so a
  // signer never has to hunt page by page.
  const unfilledFields = [...fields]
    .filter((f) => !f.filled)
    .sort((a, b) => (a.page - b.page) || (a.y - b.y) || (a.x - b.x));

  // Jump to the next unfilled field — prefer the next one at/after the current
  // page, then wrap to the start — switch to its page and pulse it.
  const goToNextField = () => {
    if (unfilledFields.length === 0) return;
    const target =
      unfilledFields.find((f) => f.page >= currentPage) || unfilledFields[0];
    setCurrentPage(target.page);
    setHighlightedFieldId(target.id);
    setTimeout(() => setHighlightedFieldId((id) => (id === target.id ? null : id)), 2500);
  };

  // After jumping to a field (and its page renders), scroll it into view —
  // the page may be taller than the modal, so the field can be below the fold.
  useEffect(() => {
    if (!highlightedFieldId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`review-field-${highlightedFieldId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [highlightedFieldId, currentPage]);

  // Open field editor. Signature fields apply the adopted reusable signature
  // (one tap, like the acknowledgement flow); text fields prompt for text.
  const openFieldEditor = (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    if (field.type === 'signature') {
      if (isPreview) {
        // Preview must not persist anything — fill with a local-only placeholder
        // instead of opening the adopt modal (which writes a UserSignature and
        // would supersede the admin's real reusable signature).
        applySignatureToField(fieldId, PREVIEW_SIGNATURE);
      } else if (userSignature) {
        applySignatureToField(fieldId, userSignature);
      } else {
        // First-time: adopt a signature, then apply it to this field.
        setPendingFieldId(fieldId);
        setOpenAdopt(true);
      }
    } else {
      setActiveFieldId(fieldId);
      setTextInputValue(field.value || '');
      setShowTextInput(true);
    }
  };

  // The PNG used to stamp a signature into a field: a data URL for typed
  // (rendered in the chosen font), or the image URL for drawn —
  // generateCompletedPdf fetches either as PNG bytes to embed.
  const signatureValueFor = (sig) => {
    if (!sig) return null;
    if (sig.method === 'drawn') return sig.image_url || null;
    if (sig.typed_name) return renderTypedSignatureToPng(sig.typed_name, sig.typed_font);
    return null;
  };

  const applySignatureToField = (fieldId, sig) => {
    const value = signatureValueFor(sig);
    if (!value) {
      toast.error('Could not apply your signature. Please try again.');
      return;
    }
    setFields(prev => prev.map(field =>
      field.id === fieldId ? { ...field, value, filled: true } : field
    ));
  };

  // Called when the adopt-signature modal returns a new signature. Applies it to
  // the pending field (first-time use) AND re-stamps every already-filled
  // signature field, so the archived PDF always matches the user_signature
  // recorded in the audit payload after a "Change".
  const handleAdopted = (sig) => {
    setUserSignature(sig || null);
    const value = signatureValueFor(sig);
    if (value) {
      setFields(prev => prev.map(f =>
        (f.type === 'signature' && (f.id === pendingFieldId || f.filled))
          ? { ...f, value, filled: true }
          : f
      ));
    }
    setPendingFieldId(null);
  };

  // Save text to field
  const saveTextToField = () => {
    if (!textInputValue.trim()) {
      toast.error('Please enter text');
      return;
    }

    setFields(fields.map(field =>
      field.id === activeFieldId
        ? { ...field, value: textInputValue, filled: true }
        : field
    ));

    setShowTextInput(false);
    setActiveFieldId(null);
    setTextInputValue('');
  };

  // Check if all fields are filled
  const allFieldsFilled = fields.length > 0 && fields.every(field => field.filled);

  // Generate completed PDF with filled fields
  const generateCompletedPdf = async () => {
    try {
      // Fetch the original PDF
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPages();

      // Fill in each field
      for (const field of fields) {
        if (!field.filled || !field.value) continue;

        const page = pages[field.page];
        if (!page) continue;

        const { width, height } = page.getSize();

        // Convert percentage coordinates to actual PDF coordinates
        const actualX = field.x * width;
        const actualY = height - (field.y * height) - field.height; // PDF coords start bottom-left

        if (field.type === 'signature' && field.value) {
          // Handle signature
          const imageBytes = await fetch(field.value).then(r => r.arrayBuffer());
          const image = await pdfDoc.embedPng(imageBytes);

          page.drawImage(image, {
            x: actualX,
            y: actualY,
            width: field.width,
            height: field.height,
          });
        } else if (field.type === 'text' && field.value) {
          // Handle text
          const { rgb } = await import('pdf-lib');
          const fontSize = 12;
          const padding = 10; // Padding from field edges

          // Get font and calculate text width
          const font = await pdfDoc.embedFont('Helvetica');
          const textWidth = font.widthOfTextAtSize(field.value, fontSize);
          const maxTextWidth = field.width - padding;

          // Center text horizontally within the field, but constrain to maxWidth
          let textX;
          if (textWidth <= maxTextWidth) {
            // Text fits - center it
            textX = actualX + (field.width - textWidth) / 2;
          } else {
            // Text too wide - left-align with padding
            textX = actualX + padding / 2;
          }

          // Calculate proper vertical position (PDF coordinates start at bottom)
          // Adjust for descenders and proper vertical centering
          const textY = actualY + (field.height - fontSize) / 2 + fontSize * 0.25;

          page.drawText(field.value, {
            x: textX,
            y: textY,
            size: fontSize,
            color: rgb(0, 0, 0),
            font: font,
            maxWidth: maxTextWidth, // Prevent overflow
          });
        }
      }

      // Return the PDF bytes
      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
    } catch (error) {
      console.error('Error generating completed PDF:', error);
      throw error;
    }
  };

  // Upload completed PDF to S3
  const uploadCompletedPdf = async (pdfBytes, fileName) => {
    // Create a File object from the PDF bytes
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const file = new File([blob], fileName, { type: 'application/pdf' });

    const formData = new FormData();
    formData.append("company_id", props.homeCompany);
    formData.append("file_name", fileName);
    formData.append("type", "lms_signature_pdf_response");

    // [1] Get presigned URL
    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      {
        headers: {
          ...axiosConfig.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    setUploadProgress(30);

    // [2] Upload file to S3 with progress tracking
    await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          30 + (progressEvent.loaded * 50) / progressEvent.total
        );
        setUploadProgress(percentCompleted);
      },
    });

    setUploadProgress(80);

    // [3] Get file URL
    const resFile = await getRightworkAPI(API_get_file_upload, {
      file_upload_uuid: fileUUID,
    });
    const fileUrl = resFile?.data?.redux?.payload?.presigned_url;

    return {
      uuid: fileUUID,
      url: fileUrl,
      name: fileName
    };
  };

  // Handle finish
  const handleFinish = async () => {
    // In preview mode, just close the modal
    if (isPreview) {
      handleClose();
      return;
    }

    if (!allFieldsFilled) {
      toast.error('Please complete all fields before finishing');
      return;
    }

    // Prevent multiple submissions
    if (isUploading) {
      return;
    }

    // Immediately show uploading state
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Generate completed PDF (20% progress)
      setUploadProgress(10);
      const pdfBytes = await generateCompletedPdf();
      setUploadProgress(20);

      // Upload to S3 (20% -> 80% progress)
      const fileName = `completed_${pdfName || 'document.pdf'}`;
      const uploadedFile = await uploadCompletedPdf(pdfBytes, fileName);
      setUploadProgress(90);

      // Call completion callback to save completion data. The signer cleared the
      // e-signature disclosure gate (hasAgreed) before reaching this screen, so
      // record that consent and the disclosure version they saw.
      if (onComplete) {
        await onComplete({
          uuid: uploadedFile.uuid,
          url: uploadedFile.url,
          name: uploadedFile.name,
          consent_agreed: true,
          consent_disclosure_version: SIGNATURE_DISCLOSURE_VERSION,
          // Link the reusable signature that was applied to the fields, so the
          // PDF flow's audit trail matches the acknowledgement flow.
          ...(userSignature && {
            user_signature_uuid: userSignature.uuid,
            signature_method: userSignature.method,
            signer_printed_name: userSignature.typed_name || "",
          }),
        });
      }

      setUploadProgress(100);

      // Show completion screen
      setIsCompleted(true);

      // Auto-close after a brief delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error('Error completing document:', error);
      setUploadError('Failed to complete document. Please try again.');
      setIsUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Prevent closing during upload (but allow closing on error)
    if (isUploading && !isCompleted && !uploadError) {
      return;
    }

    if (fields.some(f => f.filled)) {
      if (window.confirm('Are you sure you want to close? Your progress will be lost.')) {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  // Don't render modal if PDF URL is not available
  if (!pdfUrl && open && !isCompleted) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
            <div className="text-slate-500 mb-4">
              <DrawOutlinedIcon style={{ fontSize: 60 }} />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Loading Document...</h3>
            <p className="text-slate-600 text-center">
              Please wait while we prepare your document.
            </p>
          </div>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="signature-review-modal"
    >
      <Box sx={style}>
        {isUploading || isCompleted || uploadError ? (
          // Upload Progress, Completion & Error Screen
          <div className="p-8 md:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {pdfName || 'Document'}
                  </h3>
                </div>
              </div>
              <button
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                onClick={handleClose}
                disabled={isUploading && !isCompleted && !uploadError}
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            {/* Content */}
            <div className="flex flex-col items-center justify-center min-h-[300px]">
              {uploadError ? (
                // Error state
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <ErrorOutlineIcon className="text-red-600" style={{ fontSize: 48 }} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                    Upload Failed
                  </h2>
                  <p className="text-slate-600 text-center mb-4 max-w-md">
                    There was a problem submitting your signature. Please try again.
                  </p>

                  {/* Error Message */}
                  <div className="w-full max-w-md mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{uploadError}</p>
                  </div>

                  {/* Retry button */}
                  <button
                    onClick={handleFinish}
                    className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </>
              ) : isCompleted ? (
                // Success state
                <>
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 48 }} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                    Signature Submitted Successfully!
                  </h2>
                  <p className="text-slate-600 text-center mb-6 max-w-md">
                    Your signature has been recorded and the document is now complete.
                  </p>
                </>
              ) : (
                // Uploading state
                <>
                  <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mb-4">
                    <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 48 }} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                    Submitting Your Signature...
                  </h2>
                  <p className="text-slate-600 text-center mb-6 max-w-md">
                    Please wait while we process your signed document.
                  </p>

                  {/* Progress Bar */}
                  <div className="w-full max-w-md mb-2">
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#e5e7eb',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#7c3aed'
                        }
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {uploadProgress}%
                  </p>
                </>
              )}
            </div>
          </div>
        ) : !hasAgreed ? (
          // Agreement Screen
          <div className="p-8 md:p-4">
            <div className="flex items-center justify-between mb-6 md:mb-4">
              <div className="flex items-center gap-3 md:gap-2">
                <div className="w-10 h-10 md:w-8 md:h-8 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                  <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                </div>
                <div>
                  <h3 className="text-2xl md:text-lg font-bold text-slate-900">
                    Electronic Signature Agreement
                  </h3>
                  <p className="text-sm md:text-xs text-slate-600">
                    Required to review and sign documents
                  </p>
                </div>
              </div>
              <button
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shrink-0"
                onClick={handleCancel}
              >
                <CloseIcon style={{ fontSize: "20px" }} />
              </button>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 md:p-4 mb-6 md:mb-4">
              <h4 className="text-lg md:text-base font-semibold text-slate-900 mb-3 md:mb-2">
                {SIGNATURE_DISCLOSURE_HEADING}
              </h4>
              <div className="text-sm md:text-xs text-slate-700 space-y-2">
                <p>
                  By checking the box below, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {SIGNATURE_DISCLOSURE_POINTS.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <p className="mt-4">
                  {SIGNATURE_DISCLOSURE_FOOTER}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 mb-6 md:mb-4">
              <input
                type="checkbox"
                id="agree-checkbox"
                checked={checkboxChecked}
                className="mt-1 w-4 h-4 border-gray-300 rounded focus:ring-violet-500 cursor-pointer"
                style={{ accentColor: '#7c3aed' }}
                onChange={(e) => setCheckboxChecked(e.target.checked)}
              />
              <label htmlFor="agree-checkbox" className="text-sm md:text-xs text-slate-700 cursor-pointer">
                {SIGNATURE_CONSENT_LABEL}
              </label>
            </div>

            <div className="flex justify-end">
              <button
                disabled={!checkboxChecked}
                onClick={() => setHasAgreed(true)}
                className={`px-6 md:px-4 py-2.5 md:py-2 rounded-lg font-medium transition-colors md:text-sm ${
                  checkboxChecked
                    ? 'bg-violet-600 hover:bg-violet-700 text-white'
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                Continue to Document
              </button>
            </div>
          </div>
        ) : (
          // Document Review Screen
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 md:px-4 py-4 md:py-3 z-[9999]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 md:gap-2 min-w-0">
                  <div className="w-10 h-10 md:w-8 md:h-8 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-2xl md:text-lg font-bold text-slate-900 truncate">
                      {pdfName || 'Document'}
                    </h3>
                    {isPreview ? (
                      <p className="text-sm md:text-xs text-amber-600 font-medium">
                        Preview Mode - Changes will not be saved
                      </p>
                    ) : (
                      <p className="text-sm md:text-xs text-slate-600 md:hidden">
                        Click on fields to sign or fill in information
                      </p>
                    )}
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shrink-0"
                  onClick={handleCancel}
                >
                  <CloseIcon style={{ fontSize: "20px" }} />
                </button>
              </div>

              {/* Progress indicator */}
              <div className="mt-4 md:mt-3 flex items-center gap-2">
                <div className="flex-1 bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-violet-600 h-2 rounded-full transition-all"
                    style={{ width: `${(fields.filter(f => f.filled).length / fields.length) * 100}%` }}
                  ></div>
                </div>
                <p className="text-sm md:text-xs text-slate-600 whitespace-nowrap">
                  {fields.filter(f => f.filled).length} of {fields.length}
                </p>
              </div>
            </div>

            <div className="w-full px-6 md:px-3 py-6 md:py-4 space-y-6 md:space-y-4">
              {/* Adopted signature indicator — applied to each signature field */}
              {userSignature && (
                <div className="w-full flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-medium text-slate-500 shrink-0">Your signature</span>
                    {userSignature.method === "drawn" && userSignature.image_url ? (
                      <img src={userSignature.image_url} alt="Signature" style={{ maxHeight: 28, maxWidth: 160 }} />
                    ) : (
                      <span
                        className="text-slate-900 truncate"
                        style={{ fontFamily: fontFamilyForKey(userSignature.typed_font), fontSize: 20 }}
                      >
                        {userSignature.typed_name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setOpenAdopt(true)}
                    className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:bg-violet-50 px-2 py-1 rounded shrink-0"
                  >
                    <EditIcon style={{ fontSize: 14 }} />
                    Change
                  </button>
                </div>
              )}

              {/* Toolbar: page navigation + "next field" guide */}
              {(totalPages > 1 || unfilledFields.length > 0) && (
                <div className="w-full flex items-center justify-between gap-2 bg-slate-100 py-2 px-2 rounded-lg">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {totalPages > 1 && (
                      <>
                        <button
                          onClick={() => changePage('prev')}
                          disabled={currentPage === 0}
                          className={`p-1 rounded ${
                            currentPage === 0
                              ? 'text-slate-400 cursor-not-allowed'
                              : 'text-violet-700 hover:bg-violet-200'
                          }`}
                        >
                          <NavigateBeforeIcon />
                        </button>
                        <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                          Page {currentPage + 1} of {totalPages}
                        </span>
                        <button
                          onClick={() => changePage('next')}
                          disabled={currentPage === totalPages - 1}
                          className={`p-1 rounded ${
                            currentPage === totalPages - 1
                              ? 'text-slate-400 cursor-not-allowed'
                              : 'text-violet-700 hover:bg-violet-200'
                          }`}
                        >
                          <NavigateNextIcon />
                        </button>
                      </>
                    )}
                  </div>
                  {unfilledFields.length > 0 && (
                    <button
                      onClick={goToNextField}
                      className="flex items-center gap-1 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium pl-3 pr-2 py-1.5 rounded-lg shrink-0 transition-colors"
                    >
                      {fields.some((f) => f.filled) ? 'Next field' : 'Start signing'}
                      <NavigateNextIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                </div>
              )}

              {/* PDF Preview Container */}
              <div
                ref={pdfContainerRef}
                className="relative border border-slate-300 rounded-lg bg-slate-50"
                style={{
                  width: '100%',
                  // Match the real page proportions so the embedded PDF fills the
                  // box edge-to-edge and percentage coordinates map correctly
                  // (no minHeight — it would letterbox landscape pages).
                  aspectRatio: `${currentPageSize.width} / ${currentPageSize.height}`,
                }}
              >
                <object
                  key={`pdf-page-${currentPage}`}
                  data={`${pdfUrl}#page=${currentPage + 1}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  type="application/pdf"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block'
                  }}
                >
                  <iframe
                    src={`${pdfUrl}#page=${currentPage + 1}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    type="application/pdf"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                  />
                </object>

                {/* Invisible overlay to capture positioning */}
                <div
                  ref={pdfPreviewRef}
                  className="absolute inset-0"
                  style={{ zIndex: 5 }}
                />

                {/* Field Overlays */}
                {getFieldsByPage(currentPage).map(field => {
                  const scaledWidth = field.width * containerScale;
                  const scaledHeight = field.height * containerScale;
                  const scaledFontSize = Math.max(8, 12 * containerScale); // Min 8px
                  const scaledIconSize = Math.max(12, 16 * containerScale); // Min 12px
                  const borderWidth = Math.max(1, 2 * containerScale); // Min 1px
                  const isHighlighted = highlightedFieldId === field.id;

                  return (
                    <div
                      key={field.id}
                      id={`review-field-${field.id}`}
                      onClick={() => openFieldEditor(field.id)}
                      style={{
                        position: 'absolute',
                        left: `${field.x * 100}%`,
                        top: `${field.y * 100}%`,
                        width: `${scaledWidth}px`,
                        height: `${scaledHeight}px`,
                        border: `${borderWidth}px solid ${field.filled ? '#10b981' : '#ef4444'}`,
                        backgroundColor: field.filled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        // Pulse ring when the signer jumps to this field.
                        boxShadow: isHighlighted ? '0 0 0 4px rgba(124,58,237,0.55)' : 'none',
                        transition: 'box-shadow 0.2s ease',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${scaledFontSize}px`,
                        color: field.filled ? '#10b981' : '#ef4444',
                        fontWeight: '600',
                        zIndex: isHighlighted ? 15 : 10,
                        userSelect: 'none',
                      }}
                    >
                      {field.filled ? (
                        <div className="flex items-center gap-1">
                          <CheckCircleIcon style={{ fontSize: scaledIconSize }} />
                          <span>Completed</span>
                        </div>
                      ) : field.type === 'signature' ? (
                        <div className="flex flex-col items-center justify-center">
                          <span style={{ fontSize: `${scaledFontSize}px`, marginBottom: '2px' }}>
                            Sign
                          </span>
                          <DownloadIcon style={{ fontSize: scaledIconSize }} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span style={{ fontSize: `${scaledFontSize}px` }}>
                            Text
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 md:px-3 py-4 md:py-3 z-10">
              <div className="flex justify-between items-center">
                <div></div>
                <div className="flex gap-3 md:gap-2">
                  <button
                    onClick={handleCancel}
                    className="px-6 md:px-4 py-2.5 md:py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors md:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFinish}
                    disabled={!isPreview && !allFieldsFilled}
                    className={`px-6 md:px-4 py-2.5 md:py-2 rounded-lg font-medium transition-colors md:text-sm ${
                      isPreview
                        ? 'bg-violet-600 hover:bg-violet-700 text-white'
                        : allFieldsFilled
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {isPreview ? 'Exit Preview' : 'Finish & Submit'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Adopt-a-signature modal (typed or drawn, reusable) */}
        <AdoptSignatureModal
          open={openAdopt}
          handleClose={() => { setOpenAdopt(false); setPendingFieldId(null); }}
          homeCompany={props.homeCompany}
          onAdopted={handleAdopted}
        />

        {/* Text Input Modal */}
        <Modal
          open={showTextInput}
          onClose={() => setShowTextInput(false)}
          aria-labelledby="text-input-modal"
        >
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: '90%',
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
          }}>
            <div className="bg-white rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-900">Enter Text</h3>
                <button onClick={() => setShowTextInput(false)} className="text-slate-500 hover:text-slate-700">
                  <CloseIcon />
                </button>
              </div>
              <textarea
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                placeholder="Enter text for this field..."
                className="w-full border border-slate-300 rounded-lg p-3 mb-4 focus:border-violet-700 focus:outline-none"
                rows={4}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowTextInput(false);
                    setActiveFieldId(null);
                    setTextInputValue('');
                  }}
                  className="px-4 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTextToField}
                  className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
}

export default SignatureReviewModal;
